import React, { useMemo } from 'react'
import styled, {
  ThemeProvider as StyledComponentsThemeProvider,
  createGlobalStyle,
  css,
  DefaultTheme
} from 'styled-components'
import { useIsDarkMode } from '../state/user/hooks'
import { Text, TextProps } from 'rebass'
import { Colors } from './styled'

export * from './components'

const MEDIA_WIDTHS = {
  upToExtraSmall: 500,
  upToSmall: 720,
  upToMedium: 960,
  upToLarge: 1280
}

const mediaWidthTemplates: { [width in keyof typeof MEDIA_WIDTHS]: typeof css } = Object.keys(MEDIA_WIDTHS).reduce(
  (accumulator, size) => {
    ;(accumulator as any)[size] = (a: any, b: any, c: any) => css`
      @media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
        ${css(a, b, c)}
      }
    `
    return accumulator
  },
  {}
) as any

const TRANSITION = {
  fast: '.15s',
  faster: '.2s',
  medium: '.25s',
  slower: '.3s',
  slow: '.4s',
  slowest: '.5s'
}

const BORDER_RADIUS = {
  smallest: '6px',
  small: '12px',
  smaller: '16px',
  medium: '24px',
  larger: '32px'
}

export function colors(darkMode: boolean): Colors {
  return {
    darkMode,

    // base
    white: '#FFFFFF',
    black: '#000000',

    // text
    text1: darkMode ? '#FFFFFF' : '#000000',
    text2: darkMode ? '#8c8c8c' : '#373A4C',
    text3: darkMode ? '#878792' : '#8B91AF',
    text4: darkMode ? '#454559' : '#AFB3C4',
    text5: darkMode ? '#090913' : '#EDEFF7',
    textDrawer: darkMode ? '#FFFFFF' : '#3C3C43',

    // backgrounds / greys
    bg1: darkMode ? '#11111a' : '#FFFFFF',
    bg2: darkMode ? '#10131c' : '#F7F8FA',
    bg3: darkMode ? '#18191E' : '#EDEEF2',
    bg4: darkMode ? '#2A2A35' : '#C0C3D0',
    bg5: darkMode ? '#40444F' : '#8B91AF',
    bg6: darkMode ? '#030202' : '#525669',

    // gradient backgrounds
    gradient1: darkMode
      ? 'linear-gradient(91.93deg, #522BA4 2.6%, #D06CFF 100%)'
      : 'linear-gradient(91.93deg, #522BA4 2.6%, #D06CFF 100%)',

    //specialty colors
    modalBG: darkMode ? 'rgba(0,0,0,.425)' : 'rgba(0,0,0,0.3)',
    advancedBG: darkMode ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.6)',
    drawerBG: darkMode ? '#18191E' : '#FFFFFF',
    drawerMenuActiveBg: darkMode ? '#332B3E' : 'rgba(208, 108, 255, .5)',
    drawerLink: darkMode ? '#ffffff' : '#332B3E',
    switchBG: darkMode ? '#292d31' : '#f2f0ff',

    //primary colors
    primary1: darkMode ? '#a97eff' : '#a97eff',
    primary2: darkMode ? '#a97eff' : '#a97eff',
    primary3: darkMode ? '#B685F6' : '#B685F6',
    primary4: darkMode ? '#B685F670' : '#E7D6FD',
    primary5: darkMode ? '#a97eff70' : '#E7D6FD',

    // color text
    primaryText1: darkMode ? '#a97eff' : '#a97eff',

    // secondary colors
    secondary1: darkMode ? '#17000b26' : '#a97eff',
    secondary2: darkMode ? '#17000b26' : '#E7D6FD',
    secondary3: darkMode ? '#17000b26' : '#E7D6FD',

    // other
    red1: '#FD4040',
    red2: '#F82D3A',
    red3: '#D60000',
    red4: '#E12F2F',
    green1: '#27AE60',
    green2: '#21ac69',
    yellow1: '#FFE270',
    yellow2: '#F3841E',
    blue1: '#2172E5',
    purple1: '#F198FF'
  }
}

export function theme(darkMode: boolean): DefaultTheme {
  return {
    ...colors(darkMode),
    TRANSITION,
    BORDER_RADIUS,

    grids: {
      sm: 8,
      md: 12,
      lg: 24
    },

    //shadows
    shadow1: darkMode ? '#000' : '#A5ADD450',

    // media queries
    mediaWidth: mediaWidthTemplates,

    // css snippets
    flexColumnNoWrap: css`
      display: flex;
      flex-flow: column nowrap;
    `,
    flexRowNoWrap: css`
      display: flex;
      flex-flow: row nowrap;
    `
  }
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  const darkMode = useIsDarkMode()

  const themeObject = useMemo(() => theme(darkMode), [darkMode])

  return <StyledComponentsThemeProvider theme={themeObject}>{children}</StyledComponentsThemeProvider>
}

const TextWrapper = styled(Text)<{ color: keyof Colors }>`
  color: ${({ color, theme }) => (theme as any)[color]};
`

export const TYPE = {
  main(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text2'} {...props} />
  },
  link(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'primary1'} {...props} />
  },
  black(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text1'} {...props} />
  },
  white(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'white'} {...props} />
  },
  body(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={16} color={'text1'} {...props} />
  },
  largeHeader(props: TextProps) {
    return <TextWrapper fontWeight={600} fontSize={24} {...props} />
  },
  mediumHeader(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={20} {...props} />
  },
  subHeader(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={14} {...props} />
  },
  small(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={11} {...props} />
  },
  blue(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'blue1'} {...props} />
  },
  yellow(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'yellow1'} {...props} />
  },
  darkGray(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text3'} {...props} />
  },
  gray(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'bg3'} {...props} />
  },
  italic(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={12} fontStyle={'italic'} color={'text2'} {...props} />
  },
  error({ error, ...props }: { error: boolean } & TextProps) {
    return <TextWrapper fontWeight={500} color={error ? 'red1' : 'text2'} {...props} />
  }
}

export const FixedGlobalStyle = createGlobalStyle`
html, input, textarea, button {
  font-family: Rubik, sans-serif;
  font-display: fallback;
}

@supports (font-variation-settings: normal) {
  html, input, textarea, button {
    font-family: Rubik, sans-serif;
  }
}

html,
body {
  margin: 0;
  padding: 0;
  font-family: Rubik, sans-serif;
}

html {
  font-size: 16px;
  font-variant: none;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  font-feature-settings: 'ss01' on, 'ss02' on, 'cv01' on, 'cv03' on;
}

a {
  color: ${colors(false).blue1};
}

* {
  box-sizing: border-box;
  font-weight: 400;
}

button {
  user-select: none;
}

h1,h2,h3,h4,h5,h6 {
  font-size: 16px;
  margin: 0;
  padding: 0;
}

p {
  margin: 0;
  padding: 0;
}

`

export const ThemedGlobalStyle = createGlobalStyle`
:root {
  color-scheme: ${({ theme }) => (theme.darkMode ? 'dark' : 'light')};
}

html {
  transition: .5s;
  color: ${({ theme }) => theme.text1};
  background-color: ${({ theme }) => theme.bg6};
}

body {
  min-height: 100vh;
  background-position: 0 -30vh;
  background-repeat: no-repeat;
  background-image: ${({ theme }) => theme.bg6};
}

`
